import CalendarIcon from 'assets/calendar';
import { SecondaryButton } from 'components/theme/Button/Button';
import moment, { Moment } from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DayPickerPopover } from 'components/molecules/DayPickerPopover';
import { PopoverContentProps } from '@radix-ui/react-popover';

interface DatePickerButtonProps {
  date: Moment | null;
  isLoadingDates?: boolean;
  initialVisibleMonth?: Moment | null;
  onApplyButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
  onButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
  onCancelButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
  onClearButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
  onDateChange?: (date: Moment | null) => void;
  onOpenChange?: (open: boolean) => void;
  open: boolean;
  renderButton?: (
    props: React.ComponentPropsWithRef<'button'>,
  ) => React.ReactElement<HTMLButtonElement>;
  showClearButton?: boolean;
  isDayBlocked?: (date: Moment) => boolean;
  onNextMonthClick?: (date: Moment) => void;
  onPrevMonthClick?: (date: Moment) => void;
  align?: PopoverContentProps['align'];
  side?: PopoverContentProps['side'];
}

const defaultRenderButton = (props: React.ComponentProps<'button'>) => (
  <SecondaryButton onClick={props.onClick}>
    <CalendarIcon />
    {props.children}
  </SecondaryButton>
);

const DatePickerButton = ({
  date: dateProp,
  initialVisibleMonth = null,
  isDayBlocked,
  onApplyButtonClick,
  onButtonClick,
  onCancelButtonClick,
  onClearButtonClick,
  onDateChange,
  onNextMonthClick,
  onOpenChange,
  onPrevMonthClick,
  open,
  renderButton = defaultRenderButton,
  showClearButton = false,
  align = 'end',
  side = 'bottom',
  isLoadingDates,
}: DatePickerButtonProps) => {
  const { t: translate } = useTranslation();
  const [datePickerDate, setDatePickerDate] = useState<Moment | null>(dateProp);

  useEffect(() => {
    setDatePickerDate(dateProp);
  }, [dateProp]);

  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleOpenButtonClick: React.MouseEventHandler<HTMLButtonElement> = (
    event,
  ) => {
    onButtonClick?.(event);
    onOpenChange?.(true);
  };

  const handleCancelButtonClick: React.MouseEventHandler<HTMLButtonElement> = (
    event,
  ) => {
    onCancelButtonClick?.(event);
    onOpenChange?.(false);
    setDatePickerDate(dateProp);
  };

  const handleApplyButtonClick: React.MouseEventHandler<HTMLButtonElement> = (
    event,
  ) => {
    onApplyButtonClick?.(event);
    onDateChange?.(datePickerDate);
    onOpenChange?.(false);
  };

  const handleDateChange = (date: Moment | null) => {
    setDatePickerDate(date);
  };

  const dateText = dateProp ? dateProp.format('MMM DD') : translate('date');

  return (
    <Wrapper>
      <DayPickerPopover
        isLoading={isLoadingDates}
        mode="single"
        open={open}
        onOpenChange={onOpenChange}
        selected={datePickerDate?.toDate?.()}
        onSelect={(date) => handleDateChange(moment(date))}
        defaultMonth={initialVisibleMonth?.toDate?.()}
        modifiers={{
          disabled: (date) =>
            isDayBlocked?.(moment(date)) ?? isLoadingDates ?? false,
        }}
        onClearButtonClick={showClearButton ? onClearButtonClick : undefined}
        onCancelButtonClick={handleCancelButtonClick}
        onApplyButtonClick={handleApplyButtonClick}
        isClearButtonDisabled={!dateProp}
        isApplyButtonDisabled={!datePickerDate}
        className="landing-page-filter-date-picker"
        asChild
        trigger={renderButton({
          ref: buttonRef,
          onClick: handleOpenButtonClick,
          children: dateText,
        })}
        onNextClick={(date) => onNextMonthClick?.(moment(date))}
        onPrevClick={(date) => onPrevMonthClick?.(moment(date))}
        align={align}
        side={side}
        sideOffset={5}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
`;

export default DatePickerButton;
